'use client'

import { http } from '@/core/lib/fetcher/index.client'
import {
  clearClientCsrfToken,
  getClientCsrfToken,
  setClientCsrfToken,
} from '@/core/lib/fetcher/csrf.client'
import {
  NO_RETRY_READ,
  STANDARD_MUTATION,
  type RequestPolicy,
} from '@/core/lib/fetcher/request-policy'
import { z } from 'zod'
import {
  operationSchema,
  operationFailureSchema,
  segmentSchema,
  roomSchema,
} from '@/features/veluvit/schemas/api.schema'
import { MESSAGE_PAGE_SIZE } from '@/features/veluvit/lib/room-history'
import type { OperationFailure } from '@/features/veluvit/types/api.types'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/**
 * 백엔드(Django, /api/*) 연결 — 템플릿 client http 위에 이 백엔드 규약만 얹는다.
 * - 경로: 리소스 단수 + 끝 슬래시 필수 (`/room/{id}/turn/`).
 * - 인증: 세션 쿠키 + CSRF. 모든 쓰기 전에 CSRF 응답을 확인하고 client http 가 최신 쿠키 또는 응답 토큰을 붙인다.
 * - 생성 요청(턴·명령어·기록·추천·스냅)은 202 + Operation 으로 오고 `/operation/{id}/` 를 조회해 끝을 기다린다.
 */

/**
 * 읽기 정책 — 자동 재요청 없음(NO_RETRY_READ). 응답 한도만 fetcher 최대치(4MB)로 올렸다.
 * bootstrap 은 공개 작품 전체(works)를 함께 싣고 와 기본 한도(1MB)를 넘는다.
 */
const READ_POLICY: RequestPolicy = Object.freeze({
  ...NO_RETRY_READ,
  id: 'app-read-v1',
  maxResponseBytes: 4_194_304,
})
/** 쓰기 정책 — 쓰기 응답도 bootstrap·방 전체를 돌려주므로 읽기와 같은 한도를 준다 */
const WRITE_POLICY: RequestPolicy = Object.freeze({
  ...STANDARD_MUTATION,
  id: 'app-write-v1',
  maxResponseBytes: 4_194_304,
})

export class ApiError extends Error {
  status: number
  code?: string
  failure?: OperationFailure | null
  unknownOutcome: boolean
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
    this.unknownOutcome = false
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

let mutationGeneration = 0
export function getMutationGeneration() {
  return mutationGeneration
}

/** Django는 끝 슬래시가 필수다. 쿼리가 있으면 그 앞에 붙인다. */
export function apiPath(path: string) {
  const [pathname, query] = path.split('?')
  const withSlash = pathname.endsWith('/') ? pathname : `${pathname}/`
  return `${withSlash}${query ? `?${query}` : ''}`
}

/**
 * CSRF 쿠키와 헤더 토큰을 준비한다(백엔드 정의서 1단계: GET /api/user/csrf/).
 * 응답 실패나 토큰 누락은 쓰기 요청 전에 오류로 올린다.
 */
const csrfResponseSchema = z.object({
  csrfToken: z.string().regex(/^(?:[a-zA-Z0-9]{32}|[a-zA-Z0-9]{64})$/),
})
let csrfGeneration = 0
let csrfReadyGeneration: number | null = 0
let priming: { generation: number; promise: Promise<void> } | null = null
export async function primeCsrf() {
  // 인증이 바뀐 직후에는 이전 쿠키가 남아 있어도 서버에서 새 토큰을 확인한다.
  const generation = csrfGeneration
  if (
    csrfReadyGeneration === generation &&
    csrfResponseSchema.shape.csrfToken.safeParse(getClientCsrfToken()).success
  )
    return
  if (priming?.generation === generation) return priming.promise
  const promise = (async () => {
    const response = await http.get('/user/csrf/', {
      cache: 'REALTIME',
      requestPolicy: READ_POLICY,
    })
    if (csrfGeneration !== generation)
      throw new ApiError(
        '계정이 바뀌어 보안 확인을 다시 준비해야 해요.',
        0,
        'CSRF_SESSION_CHANGED'
      )
    if (!response.ok) {
      clearClientCsrfToken()
      throw new ApiError(
        '보안 확인을 준비하지 못했어요. 잠시 후 다시 시도해 주세요.',
        response.status,
        'CSRF_PREPARATION_FAILED'
      )
    }
    const parsed = csrfResponseSchema.safeParse(response.data)
    if (!parsed.success) {
      clearClientCsrfToken()
      throw new ApiError(
        '보안 확인 응답을 확인하지 못했어요.',
        502,
        'INVALID_CSRF_RESPONSE'
      )
    }
    setClientCsrfToken(parsed.data.csrfToken)
    csrfReadyGeneration = generation
  })().finally(() => {
    if (priming?.promise === promise) priming = null
  })
  priming = { generation, promise }
  await promise
}

/** 서버 오류를 사용자에게 보일 문장으로 — CSRF 실패 등은 HTML 로 오므로 그대로 보이지 않는다 */
function errorMessage(status: number, message: string | undefined) {
  const text = (message || '').trim()
  if (status === 0)
    return '전송 결과를 확인하지 못했어요. 기존 요청 상태를 확인해 주세요.'
  if (/^The (API response|request)/.test(text))
    return '서버 응답을 확인하지 못했어요. 기존 작업 상태를 다시 확인해 주세요.'
  if (status === 401)
    return text && !text.startsWith('<') && text !== 'Unauthorized'
      ? text
      : '로그인이 만료됐어요. 새로고침한 뒤 다시 시도해 주세요.'
  if (status === 403) {
    // 권한 오류는 DRF 가 JSON {error} 로 준다. HTML·'Forbidden' 은 Django CSRF 검사 실패다
    return text && !text.startsWith('<') && text !== 'Forbidden'
      ? text
      : '보안 확인에 실패했어요. 새로고침한 뒤 다시 시도해 주세요.'
  }
  if (
    !text ||
    text.startsWith('<') ||
    /^(Bad Request|Internal Server Error|Service Unavailable|Not Found)$/.test(
      text
    )
  ) {
    return status === 503
      ? '서버를 준비하고 있어요. 잠시 후 다시 시도해 주세요.'
      : '연결을 확인해 주세요.'
  }
  return text
}

const errorEnvelopeSchema = z.looseObject({
  error: z.string().optional(),
  code: z.string().optional(),
  message: z.string().optional(),
  category: z.string().optional(),
  retryable: z.boolean().optional(),
  reason: z.string().nullable().optional(),
  options: z.array(z.unknown()).optional(),
  failure: operationFailureSchema.nullable().optional(),
})

/** DRF 배열·필드 오류는 Operation이 아니다. 공개된 오류 문구만 읽는다. */
function validationMessage(value: unknown): string | undefined {
  const list = z.array(z.string()).safeParse(value)
  if (list.success) return list.data.slice(0, 3).join(' · ')
  const fields = z
    .record(z.string(), z.union([z.string(), z.array(z.string())]))
    .safeParse(value)
  if (!fields.success) return undefined
  return Object.values(fields.data).flat().slice(0, 3).join(' · ')
}

async function send<T>(
  path: string,
  body: unknown,
  method: Method,
  signal?: AbortSignal
): Promise<T> {
  let requestPath = path
  if (
    method === 'GET' &&
    /^\/(?:room|operation)\/[^/?]+\/$/.test(path.split('?')[0])
  ) {
    const [pathname, query = ''] = path.split('?')
    const params = new URLSearchParams(query)
    if (!params.has('messageLimit'))
      params.set('messageLimit', String(MESSAGE_PAGE_SIZE))
    requestPath = `${pathname}?${params}`
  }
  const url = apiPath(requestPath)
  if (method !== 'GET') {
    const generation = csrfGeneration
    await primeCsrf()
    if (generation !== csrfGeneration)
      throw new ApiError(
        '계정이 바뀌어 요청을 보내지 않았어요.',
        0,
        'CSRF_SESSION_CHANGED'
      )
  }
  const response =
    method === 'GET'
      ? await http.get<T>(url, {
          cache: 'REALTIME',
          requestPolicy: READ_POLICY,
          signal,
        })
      : method === 'POST'
        ? // FormData(파일 업로드 · /creator/me/media/)는 JSON 으로 바꾸지 않고 그대로 보낸다 — Content-Type 은 브라우저가 붙인다
          await http.post<T>(url, body ?? {}, {
            requestPolicy: WRITE_POLICY,
            signal,
            isFormData: body instanceof FormData,
          })
        : method === 'PATCH'
          ? await http.patch<T>(url, body ?? {}, {
              requestPolicy: WRITE_POLICY,
              signal,
            })
          : await http.del<T>(url, { requestPolicy: WRITE_POLICY, signal })
  if (!response.ok) {
    if (method !== 'GET' && response.status === 403) {
      csrfReadyGeneration = null
      clearClientCsrfToken()
    }
    const code = response.data?.detail
    const parsed = errorEnvelopeSchema.safeParse(response.data?.body)
    const envelope = parsed.success ? parsed.data : undefined
    const error = new ApiError(
      errorMessage(
        response.status,
        envelope?.error ||
          envelope?.message ||
          validationMessage(response.data?.body) ||
          response.message
      ),
      response.status,
      envelope?.code || (code && !code.startsWith('HTTP_') ? code : undefined)
    )
    const failure = operationFailureSchema.safeParse(
      envelope?.failure || envelope
    )
    if (failure.success) error.failure = failure.data
    error.unknownOutcome = response.data?.unknownOutcome ?? false
    throw error
  }
  const pathname = path.split('?')[0]
  if (method !== 'GET' && pathname.startsWith('/auth/')) {
    csrfGeneration += 1
    csrfReadyGeneration = null
    clearClientCsrfToken()
  }
  // 알림 읽음 · 대화방 읽음 보고는 받는 중인 bootstrap 을 버릴 이유가 없다(읽음은 화면에 바로 반영해 둔다). 그 외 쓰기는 안전하게 갱신 대상으로 둔다.
  if (
    method !== 'GET' &&
    !/^\/notification\/[^/]+\/?$|^\/room\/[^/]+\/read\/$/.test(pathname)
  )
    mutationGeneration += 1
  if (
    (method === 'POST' && pathname === '/room/') ||
    (method !== 'DELETE' && /^\/room\/[^/]+\/$/.test(pathname))
  ) {
    const parsed = roomSchema.safeParse(response.data)
    if (!parsed.success) {
      const error = new ApiError(
        '대화방 응답을 확인하지 못했어요. 기존 작업 상태를 다시 확인해 주세요.',
        502,
        'INVALID_ROOM'
      )
      error.unknownOutcome = method !== 'GET'
      throw error
    }
  }
  return response.data
}

/** 세션 API 호출. 실패하면 서버 메시지를 담은 ApiError 를 던진다(레퍼런스 api() 와 같은 계약). */
export async function api<T>(
  path: string,
  body?: unknown,
  method?: Method,
  signal?: AbortSignal
): Promise<T> {
  return send<T>(
    path,
    body,
    method || (body === undefined ? 'GET' : 'POST'),
    signal
  )
}

/* ---------- 비동기 생성(Operation) ---------- */

type OperationFields = Pick<
  z.infer<typeof operationSchema>,
  keyof typeof operationSchema.shape
>
export type Operation<R = Record<string, unknown>> = Omit<
  OperationFields,
  'result'
> & { result?: R | null } & Record<string, unknown>

export class OperationError extends ApiError {
  operation: Operation
  constructor(operation: Operation, message: string, code: string) {
    super(message, operation.status === 'invalidated' ? 409 : 500, code)
    this.operation = operation
    this.failure = operation.failure
  }
}

const streamDataSchema = z.record(z.string(), z.unknown())
export type OperationStreamEvent = {
  event: string
  id?: string
  data: z.infer<typeof streamDataSchema>
}
export type OperationOptions = {
  signal?: AbortSignal
  onOperation?: (operation: Operation) => void
  onStreamEvent?: (event: OperationStreamEvent) => void
}

const PENDING = new Set(['queued', 'running'])
const POLL_MS = 1500
/** 서버는 15분이 지나면 실패로 닫는다. 화면은 그보다 조금 더 기다린다 */
const GIVE_UP_MS = 16 * 60 * 1000

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(
        new ApiError('화면의 생성 상태 연결을 종료했어요.', 0, 'CANCELLED')
      )
      return
    }
    const abort = () => {
      clearTimeout(timer)
      reject(
        new ApiError('화면의 생성 상태 연결을 종료했어요.', 0, 'CANCELLED')
      )
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', abort, { once: true })
  })
}

function isOperation(value: unknown): value is Operation {
  return (
    !!value &&
    typeof value === 'object' &&
    'status' in value &&
    ('requestId' in value || 'id' in value)
  )
}

/** 끝난 Operation 을 예전 동기 응답 모양으로 편다 — `{...result, room, wallet}` */
function settle<T>(op: Operation): T {
  if (op.status === 'failed')
    throw new OperationError(
      op,
      op.error ||
        op.failure?.message ||
        '생성에 실패했어요. 재화는 차감되지 않았어요.',
      op.failure?.code || 'OPERATION_FAILED'
    )
  if (op.status === 'cancelled')
    throw new OperationError(
      op,
      op.error || '생성을 취소했어요.',
      'AI_CANCELLED'
    )
  if (op.status === 'invalidated')
    throw new OperationError(
      op,
      '되돌린 대화예요. 새 요청으로 보내 주세요.',
      'REQUEST_REWOUND'
    )
  if (op.status !== 'complete')
    throw new ApiError('아직 생성이 끝나지 않았어요.', 409, 'OPERATION_PENDING')
  const flat: Record<string, unknown> = { ...(op.result || {}) }
  const room = op.room ?? flat.room
  if (room !== undefined) {
    const parsed = roomSchema.safeParse(room)
    if (!parsed.success)
      throw new ApiError(
        '완료된 방의 기록을 확인하지 못했어요. 방을 새로고침해 주세요.',
        502,
        'INVALID_ROOM'
      )
    flat.room = room
  }
  if (op.wallet !== undefined) flat.wallet = op.wallet
  return flat as T
}

/**
 * 생성 요청 — 202 면 끝날 때까지 `/operation/{id}/` 를 1.5초마다 조회한다.
 * 같은 requestId 로 다시 보내면 서버는 기존 작업을 돌려주므로, 새로고침 뒤 이어서 기다리기에도 쓴다.
 */
export async function runOperation<T>(
  path: string,
  body: Record<string, unknown>,
  options: OperationOptions = {}
): Promise<T> {
  const identity = useSessionStore.getState().identity
  const first = await send<Operation | T>(path, body, 'POST', options.signal)
  if (identity !== useSessionStore.getState().identity)
    throw new ApiError(
      '계정이 바뀌어 이전 작업 연결을 종료했어요.',
      0,
      'SESSION_CHANGED'
    )
  if (!isOperation(first)) return first as T
  const parsed = operationSchema.safeParse(first)
  if (!parsed.success)
    throw new ApiError(
      '작업 정보를 확인하지 못했어요. 방을 새로고침해 주세요.',
      502,
      'INVALID_OPERATION'
    )
  return waitOperation<T>(parsed.data.id, options, parsed.data)
}

/** 재접속은 GET만 사용한다. 실패한 생성의 POST 재발행은 사용자가 선택한다. */
export async function getOperation(
  id: string,
  signal?: AbortSignal
): Promise<Operation> {
  const value = await send<unknown>(
    `/operation/${encodeURIComponent(id)}/`,
    undefined,
    'GET',
    signal
  )
  const parsed = operationSchema.safeParse(value)
  if (!parsed.success || parsed.data.id !== id)
    throw new ApiError(
      '작업 정보를 확인하지 못했어요.',
      502,
      'INVALID_OPERATION'
    )
  return parsed.data
}

export async function waitOperation<T>(
  id: string,
  options: OperationOptions = {},
  initial?: Operation
): Promise<T> {
  const controller = new AbortController()
  const identity = useSessionStore.getState().identity
  const detach = () => controller.abort()
  const unsubscribe = useSessionStore.subscribe((state) => {
    if (state.identity !== identity) detach()
  })
  if (options.signal?.aborted) detach()
  options.signal?.addEventListener('abort', detach, { once: true })
  try {
    return await followOperation<T>(
      id,
      { ...options, signal: controller.signal },
      initial
    )
  } finally {
    unsubscribe()
    options.signal?.removeEventListener('abort', detach)
  }
}

async function followOperation<T>(
  id: string,
  options: OperationOptions,
  initial?: Operation
): Promise<T> {
  let op = initial || (await getOperation(id, options.signal))
  const started = Date.now()
  options.onOperation?.(op)
  let source: EventSource | undefined
  let cursor: string | undefined
  let streamTerminal = !PENDING.has(op.status)
  const close = () => {
    source?.close()
    source = undefined
  }
  const events = [
    'reset',
    'phase',
    'segment',
    'replace',
    'complete',
    'discard',
    'reconnect',
  ]
  const connect = () => {
    if (
      streamTerminal ||
      options.signal?.aborted ||
      !options.onStreamEvent ||
      typeof EventSource === 'undefined'
    )
      return
    // 일반 JSON HTTP와 달리 SSE는 응답을 계속 읽는다. 브라우저 EventSource가 프레임 경계와 Last-Event-ID를 관리한다.
    source = new EventSource(
      `/api${apiPath(`/operation/${encodeURIComponent(id)}/stream/${cursor ? `?after=${cursor}` : ''}`)}`,
      { withCredentials: true }
    )
    for (const event of events)
      source.addEventListener(event, (message) => {
        if (
          streamTerminal ||
          options.signal?.aborted ||
          !(message instanceof MessageEvent)
        )
          return
        const sequence = message.lastEventId || undefined
        if (
          sequence &&
          (!/^\d{1,12}$/.test(sequence) ||
            (cursor && Number(sequence) <= Number(cursor)))
        )
          return
        let raw: unknown
        try {
          raw = JSON.parse(message.data)
        } catch {
          return
        }
        const parsed = streamDataSchema.safeParse(raw)
        if (!parsed.success) return
        const data = parsed.data
        if (
          event === 'segment' &&
          !z
            .object({
              index: z.number().int().nonnegative(),
              segment: segmentSchema,
            })
            .safeParse(data).success
        )
          return
        if (
          event === 'replace' &&
          !z.object({ segments: z.array(segmentSchema) }).safeParse(data)
            .success
        )
          return
        if (event === 'complete' && data.operationId !== id) return
        if (sequence) cursor = sequence
        if (event === 'complete' || event === 'discard') {
          streamTerminal = true
          close()
        }
        options.onStreamEvent?.({ event, id: sequence, data })
        if (event === 'reconnect') {
          const next = z
            .string()
            .regex(/^\d{1,12}$/)
            .safeParse(data.cursor)
          if (next.success && (!cursor || Number(next.data) >= Number(cursor)))
            cursor = next.data
          close()
        }
      })
  }
  options.signal?.addEventListener('abort', close, { once: true })
  try {
    connect()
    while (PENDING.has(op.status)) {
      if (Date.now() - started > GIVE_UP_MS)
        throw new ApiError(
          '아직 작업 완료를 확인하지 못했어요. 방을 새로고침해 기존 작업을 확인해 주세요.',
          504,
          'OPERATION_TIMEOUT'
        )
      await sleep(POLL_MS, options.signal)
      op = await getOperation(id, options.signal)
      options.onOperation?.(op)
      if (!PENDING.has(op.status)) {
        streamTerminal = true
        close()
      } else if (!source) connect()
    }
    return settle<T>(op)
  } finally {
    close()
    options.signal?.removeEventListener('abort', close)
  }
}

/** 서버 requestId 규칙: 영숫자·밑줄·하이픈 8~100자 — UUID 는 그대로 맞는다 */
export const requestId = () => crypto.randomUUID()
